import React, { useEffect, useMemo, useRef, useState } from "react";
import socket from "../lib/socket";

const VIDEO_CHEER_THRESHOLD = 300;
const SHOW_MS = 9000;

// These are the randomized code-based alerts used for 299 GAS and below.
// They are also used as the fallback for any channel that does not have custom videos.
const GENERIC_ALERT_VARIANTS = [
  "power-meter",
  "combo-breaker",
  "loot-drop",
  "hype-portal",
];

// Keep old overlay URL styles from breaking if an OBS/browser source still has ?style=impact, etc.
const LEGACY_VARIANT_ALIASES = {
  impact: "combo-breaker",
  arcade: "power-meter",
  spotlight: "loot-drop",
  comic: "hype-portal",
};

const CHANNEL_VIDEO_MAP = {
  lo_go_loco: [
    "/overlays/logoloco/batman.mp4",
    "/overlays/logoloco/quan.mp4",
    "/overlays/logoloco/take%20a%20drink.mp4",
  ],
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function hashString(input = "") {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash >>> 0;
}

function normalizeChannelKey(value) {
  return String(value || "")
    .trim()
    .replace(/^live:/i, "")
    .replace(/^vod:/i, "")
    .toLowerCase();
}

function normalizeVariant(value = "") {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  return LEGACY_VARIANT_ALIASES[raw] || raw;
}

function getDisplayName(record) {
  return (
    String(
      record?.senderUsername ||
        record?.senderName ||
        record?.senderEmail ||
        "Someone"
    ).trim() || "Someone"
  );
}

function getBits(record) {
  return Math.max(0, Number(record?.bits || 0));
}

function normalizeMessage(record) {
  return String(record?.message || "").trim().slice(0, 180);
}

function getCheerTier(bits) {
  if (bits >= VIDEO_CHEER_THRESHOLD) return "video";
  if (bits >= 200) return "high";
  if (bits >= 100) return "medium";
  return "small";
}

function normalizeAlert(record, forcedVariant) {
  const bits = getBits(record);
  const seedSource = String(
    record?.id || record?.stripeSessionId || `${Date.now()}_${bits}`
  );

  const normalizedForcedVariant = normalizeVariant(forcedVariant);
  const variant =
    normalizedForcedVariant && GENERIC_ALERT_VARIANTS.includes(normalizedForcedVariant)
      ? normalizedForcedVariant
      : GENERIC_ALERT_VARIANTS[hashString(seedSource) % GENERIC_ALERT_VARIANTS.length];

  return {
    id: String(record?.id || record?.stripeSessionId || `cheer_${Date.now()}`),
    bits,
    tier: getCheerTier(bits),
    name: getDisplayName(record),
    message: normalizeMessage(record),
    variant,
    forceCode: false,
  };
}

function pickVideoSrc(channelKey, alertId) {
  const list = CHANNEL_VIDEO_MAP[channelKey] || [];
  if (!list.length) return "";
  return list[hashString(`${channelKey}:${alertId}`) % list.length];
}

function channelHasVideoOverlays(channelKey) {
  return Boolean((CHANNEL_VIDEO_MAP[channelKey] || []).length);
}

export default function CheerOverlayLayer({
  channelId,
  room,
  forcedVariant = "",
  showDebug = false,
  showMeta = false,
  mode = "overlay",
  scale = 1,
  top = 7,
  left = 50,
  videoScale = 1,
  videoTop = 50,
  videoLeft = 50,
  videoWidth = 34,
}) {
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [debug, setDebug] = useState("booting");
  const timerRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const audioCtxRef = useRef(null);
  const videoElRef = useRef(null);

  const channelKey = useMemo(() => normalizeChannelKey(channelId), [channelId]);

  const roomCandidates = useMemo(() => {
    const explicit = String(room || "").trim();

    if (!channelKey && !explicit) return [];

    return Array.from(
      new Set(
        [
          explicit,
          channelKey ? `live:${channelKey}` : "",
          channelKey ? `vod:${channelKey}` : "",
        ].filter(Boolean)
      )
    );
  }, [room, channelKey]);

  const activeVideoSrc = useMemo(() => {
    if (!active || active.forceCode) return "";
    if (active.bits < VIDEO_CHEER_THRESHOLD) return "";

    const base = pickVideoSrc(channelKey, active.id);
    if (!base) return "";
    return `${base}?v=${encodeURIComponent(active.id)}`;
  }, [active, channelKey]);

  useEffect(() => {
    const onConnect = () => {
      console.log(
        "[GAS overlay] joining rooms:",
        roomCandidates,
        "channel:",
        channelKey
      );

      roomCandidates.forEach((nextRoom) => {
        try {
          socket.emit("join", { room: nextRoom, role: "overlay" });
        } catch {
          // ignore
        }

        try {
          socket.emit("watch:join", {
            channel: channelKey,
            room: nextRoom,
            role: "overlay",
          });
        } catch {
          // ignore
        }
      });

      setDebug(
        `connected → ${roomCandidates.join(", ") || "no-room"} • ${
          channelKey || "no-channel"
        }`
      );
    };

    const onDisconnect = () => setDebug("disconnected");

    const onCheerCreated = (record) => {
      console.log("[GAS overlay] cheer:created received", record);

      const alert = normalizeAlert(record, forcedVariant);
      if (seenIdsRef.current.has(alert.id)) return;

      seenIdsRef.current.add(alert.id);
      setQueue((prev) => [...prev, alert]);
      setDebug(`queued ${alert.id}`);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("cheer:created", onCheerCreated);

    if (!socket.connected) socket.connect();
    else onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("cheer:created", onCheerCreated);

      roomCandidates.forEach((nextRoom) => {
        try {
          socket.emit("leave", { room: nextRoom, role: "overlay" });
        } catch {
          // ignore
        }

        try {
          socket.emit("watch:leave", {
            channel: channelKey,
            room: nextRoom,
            role: "overlay",
          });
        } catch {
          // ignore
        }
      });
    };
  }, [forcedVariant, roomCandidates, channelKey]);

  useEffect(() => {
    if (active || queue.length === 0) return undefined;

    const next = queue[0];
    setActive(next);
    setQueue((prev) => prev.slice(1));
    setDebug(
      `showing ${next.id} • ${next.bits} GAS • ${
        next.bits >= VIDEO_CHEER_THRESHOLD && channelHasVideoOverlays(channelKey)
          ? "video-tier"
          : next.variant
      }`
    );
    playCheerTone(audioCtxRef, next.variant, next.bits);

    return undefined;
  }, [queue, active, channelKey]);

  useEffect(() => {
    if (!active) return undefined;

    const armFallback = (ms = SHOW_MS) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setActive(null);
        setDebug("idle");
      }, ms);
    };

    const videoEl = videoElRef.current;
    if (activeVideoSrc && videoEl && Number.isFinite(videoEl.duration) && videoEl.duration > 0) {
      armFallback(Math.ceil(videoEl.duration * 1000) + 300);
    } else {
      armFallback(SHOW_MS);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [active, activeVideoSrc]);

  const safeScale = clamp(Number(scale || 1), 0.6, 2);
  const safeTop = clamp(Number(top || 7), 0, 90);
  const safeLeft = clamp(Number(left || 50), 0, 100);
  const safeVideoScale = clamp(Number(videoScale || 1), 0.5, 2.4);
  const safeVideoTop = clamp(Number(videoTop || 50), 0, 100);
  const safeVideoLeft = clamp(Number(videoLeft || 50), 0, 100);
  const safeVideoWidth = clamp(Number(videoWidth || 34), 10, 95);

  const rootStyle =
    mode === "embedded"
      ? {
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 45,
        }
      : {
          width: "100vw",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
          background: "transparent",
          pointerEvents: "none",
        };

  return (
    <div style={rootStyle}>
      <style>{globalCss}</style>

      {active ? (
        activeVideoSrc ? (
          <div
            style={{
              position: "absolute",
              left: `${safeVideoLeft}%`,
              top: `${safeVideoTop}%`,
              transform: `translate(-50%, -50%) scale(${safeVideoScale})`,
              width: `${safeVideoWidth}vw`,
              maxWidth: mode === "embedded" ? 460 : 540,
              minWidth: mode === "embedded" ? 140 : 180,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
            }}
          >
            <video
              ref={videoElRef}
              key={`${active.id}:${activeVideoSrc}`}
              src={activeVideoSrc}
              autoPlay
              muted={false}
              playsInline
              preload="auto"
              onLoadedMetadata={(e) => {
                const ms = Math.ceil((e.currentTarget.duration || 0) * 1000) + 300;
                if (ms > 500) {
                  if (timerRef.current) clearTimeout(timerRef.current);
                  timerRef.current = setTimeout(() => {
                    setActive(null);
                    setDebug("idle");
                  }, ms);
                }
              }}
              onEnded={() => {
                setActive(null);
                setDebug("idle");
              }}
              onError={() => {
                console.log("[CHEERS overlay] video failed, falling back to code alert", activeVideoSrc);
                setDebug(`video failed → code fallback ${activeVideoSrc}`);
                setActive((prev) => (prev ? { ...prev, forceCode: true } : prev));
              }}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                background: "transparent",
                objectFit: "contain",
                mixBlendMode: "screen",
                filter:
                  mode === "embedded"
                    ? "drop-shadow(0 0 18px rgba(255, 60, 170, 0.34))"
                    : "drop-shadow(0 0 24px rgba(255, 60, 170, 0.45))",
              }}
            />

            {showMeta ? (
              <div className="ga-video-meta">
                <span className="ga-video-meta-name">{active.name}</span>
                <span className="ga-video-meta-label"> sent </span>
                <span className="ga-video-meta-bits">
                  {active.bits.toLocaleString()} GAS Cheers
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              position: "absolute",
              left: `${safeLeft}%`,
              top: `${safeTop}%`,
              transform: `translateX(-50%) scale(${safeScale})`,
              width: "min(940px, 94vw)",
            }}
          >
            <CheerCard alert={active} />
          </div>
        )
      ) : null}

      {showDebug ? (
        <div className="ga-debug-box">GAS overlay • {debug}</div>
      ) : null}
    </div>
  );
}

function CheerCard({ alert }) {
  switch (alert.variant) {
    case "power-meter":
      return <PowerMeterAlert alert={alert} />;
    case "combo-breaker":
      return <ComboBreakerAlert alert={alert} />;
    case "loot-drop":
      return <LootDropAlert alert={alert} />;
    case "hype-portal":
      return <HypePortalAlert alert={alert} />;
    default:
      return <ComboBreakerAlert alert={alert} />;
  }
}

function AlertMessage({ message, fallback = "LET'S GO!" }) {
  if (!message) return <div className="ga-code-message ga-empty-message">{fallback}</div>;
  return <div className="ga-code-message">“{message}”</div>;
}

function SparkField({ count = 18 }) {
  return (
    <div className="ga-spark-field" aria-hidden="true">
      {Array.from({ length: count }).map((_, idx) => (
        <i key={idx} style={{ "--i": idx }} />
      ))}
    </div>
  );
}

function PowerMeterAlert({ alert }) {
  const fill = clamp(alert.bits / (VIDEO_CHEER_THRESHOLD - 1), 0.12, 1);
  const fillPercent = Math.round(fill * 100);

  return (
    <div className="ga-code-alert ga-power-alert">
      <SparkField count={20} />
      <div className="ga-power-core">
        <div className="ga-power-topline">
          <span>STREAM POWER</span>
          <strong>OVERLOAD</strong>
        </div>
        <div className="ga-power-name">{alert.name}</div>
        <div className="ga-power-meter" style={{ "--fill": `${fillPercent}%` }}>
          <div className="ga-power-fill" />
          <div className="ga-power-grid">
            {Array.from({ length: 18 }).map((_, idx) => (
              <span key={idx} />
            ))}
          </div>
        </div>
        <div className="ga-power-bottom">
          <span>ENERGY SURGE</span>
          <strong>+{alert.bits.toLocaleString()} GAS Cheers</strong>
        </div>
        <AlertMessage message={alert.message} fallback="THE METER JUST SPIKED!" />
      </div>
    </div>
  );
}

function ComboBreakerAlert({ alert }) {
  const combo = clamp(Math.ceil(alert.bits / 35), 2, 9);

  return (
    <div className="ga-code-alert ga-combo-alert">
      <SparkField count={16} />
      <div className="ga-speed-lines" aria-hidden="true" />
      <div className="ga-combo-hit">POW!</div>
      <div className="ga-combo-panel">
        <div className="ga-combo-badge">{combo} HIT COMBO</div>
        <div className="ga-combo-name">{alert.name}</div>
        <div className="ga-combo-main">LANDED A SUPPORT COMBO</div>
        <div className="ga-combo-bits">+{alert.bits.toLocaleString()} GAS Cheers</div>
        <AlertMessage message={alert.message} fallback="COMBO BREAKER ENERGY!" />
      </div>
    </div>
  );
}

function LootDropAlert({ alert }) {
  return (
    <div className="ga-code-alert ga-loot-alert">
      <SparkField count={22} />
      <div className="ga-loot-rays" aria-hidden="true" />
      <div className="ga-loot-crate" aria-hidden="true">
        <span className="ga-crate-lid" />
        <span className="ga-crate-box" />
        <span className="ga-crate-star">★</span>
      </div>
      <div className="ga-loot-coins" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, idx) => (
          <i key={idx} style={{ "--i": idx }} />
        ))}
      </div>
      <div className="ga-loot-copy">
        <div className="ga-loot-label">RARE DROP</div>
        <div className="ga-loot-name">{alert.name}</div>
        <div className="ga-loot-main">DROPPED LOOT</div>
        <div className="ga-loot-bits">+{alert.bits.toLocaleString()} GAS Cheers</div>
        <AlertMessage message={alert.message} fallback="BONUS CHEST OPENED!" />
      </div>
    </div>
  );
}

function HypePortalAlert({ alert }) {
  return (
    <div className="ga-code-alert ga-portal-alert">
      <SparkField count={24} />
      <div className="ga-portal-wrap" aria-hidden="true">
        <span className="ga-portal-ring ga-ring-one" />
        <span className="ga-portal-ring ga-ring-two" />
        <span className="ga-portal-ring ga-ring-three" />
        <span className="ga-portal-core" />
      </div>
      <div className="ga-portal-copy">
        <div className="ga-portal-label">HYPE PORTAL</div>
        <div className="ga-portal-name">{alert.name}</div>
        <div className="ga-portal-main">OPENED THE CHANNEL GATE</div>
        <div className="ga-portal-bits">+{alert.bits.toLocaleString()} GAS Cheers</div>
        <AlertMessage message={alert.message} fallback="ENERGY ENTERED THE ROOM!" />
      </div>
    </div>
  );
}

function playCheerTone(audioCtxRef, variant, bits) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.055;
    master.connect(ctx.destination);

    const tonesByVariant = {
      "power-meter": [110, 220, 440, 659.25],
      "combo-breaker": [146.83, 293.66, 587.33, 783.99],
      "loot-drop": [261.63, 329.63, 392, 659.25],
      "hype-portal": [196, 392, 523.25, 987.77],
    };

    const freqs = tonesByVariant[variant] || tonesByVariant["combo-breaker"];
    const tail = clamp(bits / 4000, 0, 0.2);

    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = idx === 0 ? "sawtooth" : idx % 2 ? "triangle" : "square";
      osc.frequency.setValueAtTime(freq, now + idx * 0.075);

      gain.gain.setValueAtTime(0.0001, now + idx * 0.075);
      gain.gain.exponentialRampToValueAtTime(
        0.2 - idx * 0.025,
        now + idx * 0.075 + 0.025
      );
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        now + idx * 0.075 + 0.22 + tail
      );

      osc.connect(gain);
      gain.connect(master);

      osc.start(now + idx * 0.075);
      osc.stop(now + idx * 0.075 + 0.3 + tail);
    });
  } catch {}
}

const globalCss = `
  @keyframes gaCodeIn {
    0% { opacity: 0; transform: translateY(-36px) scale(0.72) rotate(-2deg); filter: blur(8px); }
    58% { opacity: 1; transform: translateY(7px) scale(1.04) rotate(0deg); filter: blur(0); }
    100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); filter: blur(0); }
  }

  @keyframes gaCodeOut {
    0%, 82% { opacity: 1; }
    100% { opacity: 0; transform: translateY(-18px) scale(0.96); filter: blur(4px); }
  }

  @keyframes gaScreenPunch {
    0%, 100% { transform: translate(0, 0); }
    10% { transform: translate(-5px, 2px); }
    20% { transform: translate(5px, -3px); }
    30% { transform: translate(-4px, -2px); }
    40% { transform: translate(3px, 3px); }
    50% { transform: translate(0, 0); }
  }

  @keyframes gaSparkFly {
    0% { opacity: 0; transform: translate(-50%, -50%) rotate(calc(var(--i) * 21deg)) translateX(0) scale(0.2); }
    14% { opacity: 1; }
    100% { opacity: 0; transform: translate(-50%, -50%) rotate(calc(var(--i) * 21deg)) translateX(calc(170px + (var(--i) % 5) * 34px)) scale(1); }
  }

  @keyframes gaGlowPulse {
    0%, 100% { filter: brightness(1) drop-shadow(0 0 12px rgba(255, 255, 255, 0.18)); }
    50% { filter: brightness(1.35) drop-shadow(0 0 24px rgba(255, 255, 255, 0.34)); }
  }

  @keyframes gaMeterFill {
    from { width: 0%; }
    to { width: var(--fill); }
  }

  @keyframes gaGridKick {
    0% { transform: translateX(-20px); opacity: 0.2; }
    100% { transform: translateX(0); opacity: 1; }
  }

  @keyframes gaHitPop {
    0% { opacity: 0; transform: translate(-50%, -50%) scale(0.2) rotate(-14deg); }
    35% { opacity: 1; transform: translate(-50%, -50%) scale(1.18) rotate(-7deg); }
    100% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(-7deg); }
  }

  @keyframes gaSpeedMove {
    from { transform: translateX(-18%); }
    to { transform: translateX(18%); }
  }

  @keyframes gaCrateDrop {
    0% { transform: translateY(-190px) scale(0.75); opacity: 0; }
    44% { transform: translateY(18px) scale(1.08); opacity: 1; }
    56% { transform: translateY(0) scale(1); }
    100% { transform: translateY(0) scale(1); }
  }

  @keyframes gaCrateLid {
    0%, 45% { transform: translate(-50%, 0) rotate(0deg); }
    64%, 100% { transform: translate(-67%, -44px) rotate(-18deg); }
  }

  @keyframes gaCoinBurst {
    0%, 36% { opacity: 0; transform: translate(-50%, 20px) rotate(0deg) scale(0.25); }
    50% { opacity: 1; }
    100% { opacity: 0; transform: translate(calc(-50% + (var(--i) - 7) * 18px), calc(-130px - (var(--i) % 4) * 28px)) rotate(420deg) scale(1); }
  }

  @keyframes gaRaysSpin {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }

  @keyframes gaPortalSpin {
    from { transform: translate(-50%, -50%) rotate(0deg) scale(0.96); }
    to { transform: translate(-50%, -50%) rotate(360deg) scale(1.04); }
  }

  @keyframes gaPortalOpen {
    0% { opacity: 0; transform: translate(-50%, -50%) scale(0.12); }
    38% { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
    100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }

  @keyframes gaVideoMetaIn {
    0% { opacity: 0; transform: translateY(10px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  .ga-code-alert {
    position: relative;
    min-height: 260px;
    color: #fff;
    font-family: Arial, Helvetica, sans-serif;
    animation: gaCodeIn 0.48s cubic-bezier(.14,1.32,.35,1), gaCodeOut ${SHOW_MS}ms ease-in forwards;
  }

  .ga-code-alert,
  .ga-code-alert * {
    box-sizing: border-box;
  }

  .ga-spark-field {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 5;
  }

  .ga-spark-field i {
    --spark-delay: calc((var(--i) % 6) * 48ms);
    position: absolute;
    left: 50%;
    top: 50%;
    width: 9px;
    height: 22px;
    border-radius: 999px;
    background: linear-gradient(180deg, #fff, #ffd166, #ff2c59);
    box-shadow: 0 0 14px rgba(255, 209, 102, 0.85);
    transform-origin: center;
    animation: gaSparkFly 1.22s ease-out var(--spark-delay) infinite;
  }

  .ga-code-message {
    margin-top: 12px;
    max-width: 760px;
    color: rgba(255, 255, 255, 0.92);
    font-size: clamp(15px, 1.9vw, 20px);
    font-weight: 800;
    line-height: 1.25;
    text-shadow: 0 2px 10px rgba(0,0,0,0.35);
    word-break: break-word;
  }

  .ga-empty-message {
    color: #ffd166;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .ga-power-alert {
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 20px 34px rgba(255, 42, 88, 0.34));
  }

  .ga-power-core {
    position: relative;
    width: min(880px, 100%);
    padding: 28px;
    border-radius: 30px;
    overflow: hidden;
    background:
      linear-gradient(135deg, rgba(16, 8, 12, 0.96), rgba(80, 4, 22, 0.92), rgba(8, 8, 13, 0.96)),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 18px);
    border: 2px solid rgba(255, 99, 132, 0.72);
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.06), 0 0 34px rgba(255, 44, 89, 0.28);
    animation: gaScreenPunch 0.42s ease-out 0.18s;
  }

  .ga-power-core::before {
    content: "";
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle, rgba(255, 209, 102, 0.28), transparent 55%);
    animation: gaRaysSpin 4s linear infinite;
  }

  .ga-power-core > * {
    position: relative;
    z-index: 2;
  }

  .ga-power-topline,
  .ga-power-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    color: rgba(255,255,255,0.82);
    font-size: clamp(12px, 1.5vw, 16px);
    font-weight: 900;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .ga-power-topline strong,
  .ga-power-bottom strong {
    color: #ffd166;
    text-shadow: 0 0 18px rgba(255, 209, 102, 0.48);
  }

  .ga-power-name {
    margin: 16px 0 14px;
    font-size: clamp(30px, 5vw, 58px);
    font-weight: 950;
    line-height: 0.95;
    text-transform: uppercase;
    text-shadow: 0 4px 0 rgba(0,0,0,0.28), 0 0 24px rgba(255,255,255,0.16);
  }

  .ga-power-meter {
    --fill: 55%;
    position: relative;
    height: 54px;
    border-radius: 18px;
    overflow: hidden;
    background: rgba(0,0,0,0.48);
    border: 1px solid rgba(255,255,255,0.14);
    box-shadow: inset 0 0 18px rgba(0,0,0,0.45);
  }

  .ga-power-fill {
    position: absolute;
    inset: 0 auto 0 0;
    width: var(--fill);
    border-radius: inherit;
    background: linear-gradient(90deg, #ff2c59, #ff8a00, #ffd166, #ffffff);
    box-shadow: 0 0 30px rgba(255, 138, 0, 0.48);
    animation: gaMeterFill 0.72s ease-out;
  }

  .ga-power-grid {
    position: absolute;
    inset: 0;
    display: grid;
    grid-template-columns: repeat(18, 1fr);
    gap: 4px;
    padding: 5px;
    animation: gaGridKick 0.7s ease-out;
  }

  .ga-power-grid span {
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.18);
    background: rgba(255,255,255,0.08);
  }

  .ga-power-bottom {
    margin-top: 14px;
  }

  .ga-combo-alert {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 310px;
    filter: drop-shadow(0 22px 34px rgba(0, 210, 255, 0.26));
  }

  .ga-speed-lines {
    position: absolute;
    inset: 20px 0;
    z-index: 0;
    overflow: hidden;
    border-radius: 40px;
    background:
      repeating-linear-gradient(160deg, rgba(255,255,255,0.0) 0 22px, rgba(255,255,255,0.16) 22px 26px),
      linear-gradient(90deg, rgba(0,210,255,0), rgba(0,210,255,0.22), rgba(255,44,89,0.18), rgba(0,210,255,0));
    animation: gaSpeedMove 0.5s linear infinite alternate;
  }

  .ga-combo-hit {
    position: absolute;
    left: 17%;
    top: 35%;
    z-index: 4;
    display: grid;
    place-items: center;
    width: clamp(120px, 19vw, 190px);
    height: clamp(92px, 15vw, 148px);
    color: #141414;
    font-size: clamp(34px, 6vw, 76px);
    font-weight: 950;
    letter-spacing: -0.06em;
    text-transform: uppercase;
    text-shadow: 2px 2px 0 #fff, 4px 4px 0 rgba(0,0,0,0.18);
    background: #ffd166;
    clip-path: polygon(8% 26%, 28% 15%, 35% 0, 50% 14%, 68% 5%, 74% 24%, 94% 22%, 82% 45%, 100% 62%, 76% 70%, 79% 94%, 55% 82%, 40% 100%, 30% 78%, 5% 86%, 17% 61%, 0 48%);
    animation: gaHitPop 0.5s cubic-bezier(.18,1.48,.34,1) 0.1s both;
  }

  .ga-combo-panel {
    position: relative;
    z-index: 3;
    width: min(760px, 84%);
    margin-left: auto;
    padding: 28px 30px 26px 150px;
    border-radius: 30px;
    border: 3px solid rgba(255,255,255,0.85);
    background: linear-gradient(135deg, rgba(6,18,30,0.95), rgba(15,67,95,0.94), rgba(18,8,22,0.96));
    box-shadow: inset 0 0 0 4px rgba(0,0,0,0.28), 0 0 34px rgba(0, 210, 255, 0.25);
    animation: gaScreenPunch 0.46s ease-out 0.2s;
  }

  .ga-combo-badge {
    display: inline-flex;
    padding: 8px 14px;
    border-radius: 999px;
    color: #06121e;
    background: linear-gradient(90deg, #6ef0ff, #ffffff);
    font-size: 13px;
    font-weight: 950;
    letter-spacing: 0.14em;
  }

  .ga-combo-name,
  .ga-loot-name,
  .ga-portal-name {
    margin-top: 10px;
    color: #fff;
    font-size: clamp(30px, 4.6vw, 56px);
    font-weight: 950;
    line-height: 0.98;
    text-transform: uppercase;
  }

  .ga-combo-main,
  .ga-loot-main,
  .ga-portal-main {
    margin-top: 8px;
    color: rgba(255,255,255,0.82);
    font-size: clamp(14px, 1.8vw, 20px);
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .ga-combo-bits,
  .ga-loot-bits,
  .ga-portal-bits {
    margin-top: 6px;
    color: #ffd166;
    font-size: clamp(28px, 4vw, 48px);
    font-weight: 950;
    line-height: 1;
    text-shadow: 0 0 22px rgba(255, 209, 102, 0.4);
  }

  .ga-loot-alert {
    min-height: 360px;
    filter: drop-shadow(0 22px 35px rgba(255, 209, 102, 0.28));
  }

  .ga-loot-rays {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 560px;
    height: 560px;
    border-radius: 50%;
    background: conic-gradient(from 0deg, rgba(255,209,102,0.4), transparent 12%, rgba(255,44,89,0.28), transparent 28%, rgba(255,209,102,0.34), transparent 44%);
    filter: blur(2px);
    animation: gaRaysSpin 6s linear infinite;
    opacity: 0.62;
  }

  .ga-loot-crate {
    position: absolute;
    left: 20%;
    top: 47%;
    width: 180px;
    height: 150px;
    z-index: 4;
    animation: gaCrateDrop 0.72s cubic-bezier(.14,1.3,.31,1) both;
  }

  .ga-crate-lid,
  .ga-crate-box {
    position: absolute;
    left: 50%;
    border-radius: 18px;
    background:
      linear-gradient(135deg, #ffd166, #ff8a00 48%, #8b3f00),
      repeating-linear-gradient(45deg, rgba(0,0,0,0.13) 0 8px, transparent 8px 18px);
    border: 3px solid rgba(255,255,255,0.78);
    box-shadow: inset 0 -12px 20px rgba(0,0,0,0.24), 0 14px 30px rgba(0,0,0,0.3);
  }

  .ga-crate-lid {
    top: 8px;
    width: 160px;
    height: 42px;
    transform: translateX(-50%);
    transform-origin: left bottom;
    animation: gaCrateLid 1.2s ease-out 0.28s both;
  }

  .ga-crate-box {
    bottom: 0;
    width: 174px;
    height: 104px;
    transform: translateX(-50%);
  }

  .ga-crate-star {
    position: absolute;
    left: 50%;
    top: 78px;
    transform: translateX(-50%);
    color: #fff;
    font-size: 48px;
    text-shadow: 0 0 18px rgba(255,255,255,0.64);
    animation: gaGlowPulse 1s ease-in-out infinite;
  }

  .ga-loot-coins {
    position: absolute;
    left: 20%;
    top: 42%;
    z-index: 6;
    pointer-events: none;
  }

  .ga-loot-coins i {
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 28%, #fff, #ffd166 40%, #ff8a00 76%);
    box-shadow: 0 0 14px rgba(255,209,102,0.72);
    animation: gaCoinBurst 1.55s ease-out calc((var(--i) % 4) * 70ms) infinite;
  }

  .ga-loot-copy {
    position: relative;
    z-index: 5;
    width: min(700px, 72%);
    margin-left: auto;
    padding: 28px 30px 26px;
    border-radius: 32px;
    background: linear-gradient(135deg, rgba(28,18,6,0.95), rgba(86,48,0,0.9), rgba(22,8,12,0.96));
    border: 2px solid rgba(255, 209, 102, 0.7);
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.06), 0 0 34px rgba(255, 209, 102, 0.22);
  }

  .ga-loot-label,
  .ga-portal-label {
    display: inline-flex;
    padding: 8px 15px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 950;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .ga-loot-label {
    color: #231500;
    background: linear-gradient(90deg, #ffd166, #ffffff);
  }

  .ga-portal-alert {
    min-height: 370px;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 22px 38px rgba(201, 56, 255, 0.32));
  }

  .ga-portal-wrap {
    position: absolute;
    left: 50%;
    top: 50%;
    width: min(520px, 65vw);
    height: min(520px, 65vw);
    z-index: 1;
    animation: gaPortalOpen 0.62s cubic-bezier(.18,1.25,.35,1) both;
  }

  .ga-portal-ring,
  .ga-portal-core {
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }

  .ga-ring-one {
    width: 100%;
    height: 100%;
    border: 9px dashed rgba(255, 44, 184, 0.82);
    box-shadow: 0 0 32px rgba(255,44,184,0.45), inset 0 0 38px rgba(255,44,184,0.24);
    animation: gaPortalSpin 3s linear infinite;
  }

  .ga-ring-two {
    width: 78%;
    height: 78%;
    border: 5px solid rgba(110, 240, 255, 0.78);
    box-shadow: 0 0 28px rgba(110,240,255,0.4), inset 0 0 30px rgba(110,240,255,0.18);
    animation: gaPortalSpin 2.1s linear infinite reverse;
  }

  .ga-ring-three {
    width: 56%;
    height: 56%;
    border: 7px dotted rgba(255, 209, 102, 0.88);
    box-shadow: 0 0 24px rgba(255,209,102,0.38);
    animation: gaPortalSpin 1.55s linear infinite;
  }

  .ga-portal-core {
    width: 44%;
    height: 44%;
    background: radial-gradient(circle, rgba(255,255,255,0.92), rgba(110,240,255,0.32) 42%, rgba(255,44,184,0.18) 62%, transparent 75%);
    filter: blur(1px);
    animation: gaGlowPulse 0.9s ease-in-out infinite;
  }

  .ga-portal-copy {
    position: relative;
    z-index: 4;
    width: min(720px, 86%);
    padding: 30px 34px 28px;
    border-radius: 34px;
    text-align: center;
    background: linear-gradient(135deg, rgba(13,8,24,0.78), rgba(62,12,85,0.82), rgba(7,16,30,0.78));
    border: 2px solid rgba(234,151,255,0.7);
    box-shadow: inset 0 0 0 2px rgba(255,255,255,0.06), 0 0 42px rgba(201, 56, 255, 0.28);
    backdrop-filter: blur(8px);
    animation: gaScreenPunch 0.42s ease-out 0.2s;
  }

  .ga-portal-label {
    color: #fff;
    background: linear-gradient(90deg, #cf38ff, #ff4db8, #6ef0ff);
  }

  .ga-video-meta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.72);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #fff;
    font: 800 14px/1 Arial, sans-serif;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    animation: gaVideoMetaIn 180ms ease-out;
    backdrop-filter: blur(8px);
    box-shadow: 0 10px 28px rgba(0,0,0,0.28);
  }

  .ga-video-meta-name {
    color: #ffffff;
  }

  .ga-video-meta-label {
    color: rgba(255,255,255,0.8);
  }

  .ga-video-meta-bits {
    color: #ff73d2;
  }

  .ga-debug-box {
    position: fixed;
    left: 12px;
    bottom: 12px;
    padding: 8px 12px;
    border-radius: 12px;
    background: rgba(0,0,0,0.72);
    color: #fff;
    font: 12px/1.3 Arial, sans-serif;
    max-width: 520px;
    z-index: 9999;
  }

  @media (max-width: 680px) {
    .ga-code-alert {
      min-height: 280px;
    }

    .ga-combo-hit {
      left: 50%;
      top: 12%;
    }

    .ga-combo-panel {
      width: 100%;
      padding: 86px 20px 22px;
      text-align: center;
    }

    .ga-loot-crate,
    .ga-loot-coins {
      left: 50%;
      top: 18%;
      transform: translateX(-50%) scale(0.72);
    }

    .ga-loot-copy {
      width: 100%;
      margin: 120px auto 0;
      text-align: center;
    }

    .ga-power-core,
    .ga-portal-copy {
      padding: 22px 20px;
    }

    .ga-power-topline,
    .ga-power-bottom {
      align-items: flex-start;
      flex-direction: column;
      gap: 6px;
    }
  }
`;
